import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Security as SecurityIcon,
  BugReport as BugIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

// Import issues data
import issuesData from './issuesData';

const IssuesDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  // Filter issues based on active tab and search term
  const getFilteredIssues = () => {
    let filteredIssues = [];
    
    switch (activeTab) {
      case 0: // All
        filteredIssues = issuesData;
        break;
      case 1: // Security
        filteredIssues = issuesData.filter(issue => issue.type === 'Security');
        break;
      case 2: // UI
        filteredIssues = issuesData.filter(issue => issue.type === 'UI');
        break;
      case 3: // Logic
        filteredIssues = issuesData.filter(issue => issue.type === 'Logic');
        break;
      case 4: // API
        filteredIssues = issuesData.filter(issue => issue.type === 'API');
        break;
      case 5: // Data
        filteredIssues = issuesData.filter(issue => issue.type === 'Data');
        break;
      default:
        filteredIssues = issuesData;
    }
    
    // Apply search filter if search term exists
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredIssues = filteredIssues.filter(issue => 
        issue.id.toLowerCase().includes(term) ||
        issue.description.toLowerCase().includes(term) ||
        issue.details.toLowerCase().includes(term)
      );
    }
    
    return filteredIssues;
  };
  
  // Get chip color based on issue type
  const getChipColor = (type) => {
    switch (type) {
      case 'Security':
        return 'error';
      case 'UI':
        return 'primary';
      case 'Logic':
        return 'warning';
      case 'API':
        return 'info';
      case 'Data':
        return 'success';
      default:
        return 'default';
    }
  };
  
  // Get icon based on issue type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'Security':
        return <SecurityIcon fontSize="small" />;
      case 'UI':
        return <BugIcon fontSize="small" />;
      case 'Logic':
        return <CodeIcon fontSize="small" />;
      case 'API':
        return <ApiIcon fontSize="small" />;
      case 'Data':
        return <StorageIcon fontSize="small" />;
      default:
        return null;
    }
  };
  
  const filteredIssues = getFilteredIssues();
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        System Issues Dashboard
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search issues..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="issue categories">
          <Tab label="All Issues" />
          <Tab label="Security" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="UI" icon={<BugIcon />} iconPosition="start" />
          <Tab label="Logic" icon={<CodeIcon />} iconPosition="start" />
          <Tab label="API" icon={<ApiIcon />} iconPosition="start" />
          <Tab label="Data" icon={<StorageIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="issues table">
          <TableHead>
            <TableRow>
              <TableCell>Issue ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIssues.map((issue) => (
              <TableRow key={issue.id} hover>
                <TableCell component="th" scope="row">
                  <Typography variant="body2" fontWeight="bold">
                    {issue.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getTypeIcon(issue.type)}
                    label={issue.type}
                    color={getChipColor(issue.type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={issue.status}
                    color={issue.status === 'Open' ? 'warning' : 'success'}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>{issue.description}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {issue.details}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {filteredIssues.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    No issues found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default IssuesDashboard;
